import Typography, { TypographyProps } from '@ui/components/atoms/typography/typography.atom';
import { styled } from '@ui/utils/style.util';
import { ComponentProps, JSX, memo, useMemo } from 'react';
import {
  Control,
  FieldValues,
  get as getFormItem,
  useFormContext,
  useFormState,
  UseFormStateProps,
} from 'react-hook-form';

// #region Types
export type FormErrorMessageProps<
  FieldType extends FieldValues,
  Type extends keyof JSX.IntrinsicElements = 'p',
> = TypographyProps<Type> &
  UseFormStateProps<FieldType> & {
    errorMessage?: string;
    errorMessageSeparator?: string;
    isInvalid?: boolean;
  };
// #endregion

// #region Sub Components
const ErrorMessage = styled(Typography.BodyTwo, 'text-red-deep');

function ErrorMessageWithForm({
  control,
  disabled,
  errorMessage: passedErrorMessage,
  errorMessageSeparator = ', ',
  exact,
  isInvalid,
  name,
  ...rest
}: FormErrorMessageProps<FieldValues>) {
  // #region Computed Values
  const { errors } = useFormState({ control, disabled: !!control && disabled, exact, name });
  const errorMessage = useMemo(() => {
    if (isInvalid === false) return '';
    if (passedErrorMessage != null) return passedErrorMessage;
    if (name == null) return '';

    const names = Array.isArray(name) ? name : [name as string];

    return (
      names
        ?.filter((item) => !!item && !Array.isArray(item))
        .map((item) => getFormItem(errors, item)?.message)
        .filter(Boolean)
        .join(errorMessageSeparator) ?? ''
    );
  }, [passedErrorMessage, errors, isInvalid, name, errorMessageSeparator]);
  // #endregion

  return (
    !!errorMessage && (
      <ErrorMessage
        {...({ ...rest, children: errorMessage } as ComponentProps<typeof ErrorMessage>)}
      />
    )
  );
}
ErrorMessageWithForm.displayName = 'ErrorMessageWithForm';
// #endregion

function BaseFormErrorMessage<
  FieldType extends FieldValues,
  Type extends keyof JSX.IntrinsicElements = 'p',
>({
  control,
  disabled,
  errorMessage,
  exact,
  isInvalid,
  name,
  ...rest
}: FormErrorMessageProps<FieldType, Type>) {
  const methods = useFormContext<FieldType>();

  return (control || methods) && name ? (
    <ErrorMessageWithForm
      control={(control ?? methods?.control) as Control<FieldValues>}
      disabled={disabled}
      errorMessage={errorMessage}
      exact={exact}
      isInvalid={isInvalid}
      name={name}
      {...(rest as ComponentProps<typeof ErrorMessageWithForm>)}
    />
  ) : (
    !!errorMessage && (
      <ErrorMessage
        {...({ ...rest, children: errorMessage } as ComponentProps<typeof ErrorMessage>)}
      />
    )
  );
}
BaseFormErrorMessage.displayName = 'FormErrorMessage';

const FormErrorMessage = memo(BaseFormErrorMessage) as typeof BaseFormErrorMessage;

export default FormErrorMessage;
